import React, { useState, useEffect, useRef } from 'react'
import { Toast } from '../../components/ui/Toast.tsx'
import { WorkflowService } from '../../services/workflowService'
import type { WorkflowModelVersion, WorkflowTransition, CreateWorkflowRequest, UpdateWorkflowRequest, WorkflowLintResult, StateDefinition } from '../../services/workflowService'

interface WorkflowDetailPageProps {
  workflowVersionId?: string
  onNavigate: (page: string, data?: any) => void
}

type TabType = 'flow' | 'import-export' | 'lint'

export function WorkflowDetailPage({ workflowVersionId, onNavigate }: WorkflowDetailPageProps) {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [workflow, setWorkflow] = useState<WorkflowModelVersion | null>(null)
  const [activeTab, setActiveTab] = useState<TabType>('flow')

  // Form data
  const [workflowName, setWorkflowName] = useState('')
  const [description, setDescription] = useState('')
  const [states, setStates] = useState<StateDefinition[]>([])
  const [transitions, setTransitions] = useState<WorkflowTransition[]>([])

  // Lint results
  const [lintResult, setLintResult] = useState<WorkflowLintResult | null>(null)
  const [lintLoading, setLintLoading] = useState(false)

  // Import/Export
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importPreview, setImportPreview] = useState<{states: StateDefinition[], transitions: WorkflowTransition[]} | null>(null)
  const [importLintResult, setImportLintResult] = useState<WorkflowLintResult | null>(null)

  // Toast
  const [toastMessage, setToastMessage] = useState('')
  const [showToast, setShowToast] = useState(false)

  // Duplicate confirm dialog
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false)

  // Refs
  const workflowNameInputRef = useRef<HTMLInputElement>(null)

  const isNew = !workflowVersionId
  const isDraft = !workflow || workflow.status === 'DRAFT'

  useEffect(() => {
    if (workflowVersionId) {
      loadWorkflow()
    }
  }, [workflowVersionId])

  const loadWorkflow = async () => {
    if (!workflowVersionId) return

    try {
      setLoading(true)
      setError(null)
      const data = await WorkflowService.getById(workflowVersionId)
      setWorkflow(data)
      setWorkflowName(data.workflowName)
      setDescription(data.description || '')
      // Parse JSON strings from backend
      setStates(typeof data.states === 'string' ? JSON.parse(data.states) : (data.states || []))
      setTransitions(typeof data.transitions === 'string' ? JSON.parse(data.transitions) : (data.transitions || []))
    } catch (err) {
      console.error('Failed to load workflow:', err)
      setError('ワークフローの取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!workflowName) {
      if (workflowNameInputRef.current) {
        workflowNameInputRef.current.setCustomValidity('ステータスフロー名を入力してください')
        workflowNameInputRef.current.reportValidity()
      }
      return
    }

    try {
      setSaving(true)
      setError(null)

      if (isNew) {
        const request: CreateWorkflowRequest = {
          workflowName,
          description: description || undefined,
          states,
          transitions
        }
        const created = await WorkflowService.create(request)
        setToastMessage('ステータスフローを作成しました')
        setShowToast(true)
        // 作成後、詳細ページへ遷移
        onNavigate('workflow-detail', { workflowVersionId: created.workflowVersionId })
      } else if (workflow) {
        const request: UpdateWorkflowRequest = {
          workflowName,
          description: description || undefined,
          states,
          transitions,
          version: workflow.version
        }
        await WorkflowService.update(workflow.workflowVersionId, request)
        setToastMessage('ステータスフローを保存しました')
        setShowToast(true)
        await loadWorkflow()
      }
    } catch (err) {
      console.error('Save failed:', err)
      setError(isNew ? '作成に失敗しました' : '更新に失敗しました')
    } finally {
      setSaving(false)
    }
  }

  const handleLint = async () => {
    try {
      setLintLoading(true)
      setError(null)

      // 新規作成時は現在のstates/transitionsを、既存の場合はIDを使用
      const result = workflow
        ? await WorkflowService.lint(workflow.workflowVersionId)
        : await WorkflowService.lint({ states, transitions })

      setLintResult(result)
      setActiveTab('lint')
    } catch (err) {
      console.error('Lint failed:', err)
      setError('整合性チェックに失敗しました')
    } finally {
      setLintLoading(false)
    }
  }

  const handlePublish = async () => {
    if (!workflow) return

    try {
      setSaving(true)
      setError(null)

      // 確定前に自動検証
      const lintResult = await WorkflowService.lint({
        states,
        transitions
      })

      if (!lintResult.pass) {
        const errorMsg = `検証エラー:\n${lintResult.errors.join('\n')}`
        setError(errorMsg)
        setSaving(false)
        return
      }

      // 検証成功したら確定
      await WorkflowService.publish(workflow.workflowVersionId)
      await loadWorkflow()
      // 確定後、割り当て画面への導線を提示
      alert('確定しました。次に「ステータス／承認ルート 割り当て」画面で申請種別との紐付けを行ってください。')
    } catch (err: any) {
      console.error('Publish failed:', err)
      setError('確定に失敗しました: ' + (err.message || ''))
    } finally {
      setSaving(false)
    }
  }

  const handleDuplicateClick = () => {
    setShowDuplicateConfirm(true)
  }

  const handleDuplicateConfirm = async () => {
    if (!workflow) return

    try {
      setSaving(true)
      setError(null)
      setShowDuplicateConfirm(false)
      const newWorkflow = await WorkflowService.duplicate(workflow.workflowVersionId)
      // 複製後、新しいワークフローの詳細ページへ遷移
      onNavigate('workflow-detail', { workflowVersionId: newWorkflow.workflowVersionId })
    } catch (err) {
      console.error('Duplicate failed:', err)
      setError('複製に失敗しました')
    } finally {
      setSaving(false)
    }
  }

  // State management
  const handleAddState = () => {
    const newOrder = states.length + 1
    setStates([...states, {
      key: '',
      label: '',
      bgColor: '#E2E8F0',
      textColor: '#1E293B',
      terminal: false,
      order: newOrder
    }])
  }

  const handleRemoveState = (index: number) => {
    if (!window.confirm('このステータスを削除してもよろしいですか？')) {
      return
    }
    const newStates = states.filter((_, i) => i !== index)
    // Update order for remaining states
    newStates.forEach((state, i) => {
      state.order = i + 1
    })
    setStates(newStates)
  }

  const handleStateChange = (index: number, field: keyof StateDefinition, value: any) => {
    const newStates = [...states]
    newStates[index] = { ...newStates[index], [field]: value }
    setStates(newStates)
  }

  const handleMoveStateUp = (index: number) => {
    if (index === 0) return
    const newStates = [...states]
    ;[newStates[index - 1], newStates[index]] = [newStates[index], newStates[index - 1]]
    // Update order
    newStates.forEach((state, i) => {
      state.order = i + 1
    })
    setStates(newStates)
  }

  const handleMoveStateDown = (index: number) => {
    if (index === states.length - 1) return
    const newStates = [...states]
    ;[newStates[index], newStates[index + 1]] = [newStates[index + 1], newStates[index]]
    // Update order
    newStates.forEach((state, i) => {
      state.order = i + 1
    })
    setStates(newStates)
  }

  // Transition management
  const handleAddTransition = () => {
    setTransitions([...transitions, { from: '', to: '', action: '' }])
  }

  const handleRemoveTransition = (index: number) => {
    if (!window.confirm('この遷移を削除してもよろしいですか？')) {
      return
    }
    setTransitions(transitions.filter((_, i) => i !== index))
  }

  const handleTransitionChange = (index: number, field: keyof WorkflowTransition, value: string) => {
    const newTransitions = [...transitions]
    newTransitions[index] = { ...newTransitions[index], [field]: value }
    setTransitions(newTransitions)
  }

  // Export workflow definition
  const handleExport = () => {
    const exportData = {
      states,
      transitions
    }

    const jsonString = JSON.stringify(exportData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)

    const date = new Date().toISOString().split('T')[0].replace(/-/g, '')
    const filename = `workflow-${workflow?.workflowVersionId || 'new'}-${date}.json`

    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  // Import workflow definition
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setImportFile(file)
    setImportPreview(null)
    setImportLintResult(null)

    try {
      const text = await file.text()
      const data = JSON.parse(text)

      // Basic validation
      if (!data.states || !Array.isArray(data.states)) {
        setError('インポートファイルの形式が正しくありません: states が必要です')
        return
      }
      if (!data.transitions || !Array.isArray(data.transitions)) {
        setError('インポートファイルの形式が正しくありません: transitions が必要です')
        return
      }

      setImportPreview(data)
      setError(null)

      // Run lint on imported data
      if (workflow) {
        // Create temporary workflow data for lint
        const tempWorkflow = {
          ...workflow,
          states: JSON.stringify(data.states),
          transitions: JSON.stringify(data.transitions)
        }

        // Note: We need to call lint API with temp data
        // For now, just set preview
      }
    } catch (err) {
      console.error('Failed to parse import file:', err)
      setError('インポートファイルのJSON解析に失敗しました')
    }
  }

  const handleApplyImport = async () => {
    if (!importPreview) return

    try {
      // インポート適用前に自動検証
      const lintResult = await WorkflowService.lint({
        states: importPreview.states,
        transitions: importPreview.transitions
      })

      if (!lintResult.pass) {
        const errorMsg = `インポートファイルの検証エラー:\n${lintResult.errors.join('\n')}`
        setError(errorMsg)
        return
      }

      // 検証成功したら適用
      setStates(importPreview.states)
      setTransitions(importPreview.transitions)
      setImportFile(null)
      setImportPreview(null)
      setImportLintResult(null)
      setActiveTab('flow')
      setError(null)
    } catch (err) {
      console.error('Import validation failed:', err)
      setError('インポートの検証に失敗しました')
    }
  }

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-center text-gray-500">読み込み中...</div>
      </div>
    )
  }

  return (
    <div className="px-8 py-6">
      <Toast
        message={toastMessage}
        show={showToast}
        onClose={() => setShowToast(false)}
      />

      <div className="page-header">
        <button
          onClick={() => onNavigate('workflows-list')}
          className="text-blue-600 hover:text-blue-800 flex items-center gap-2"
          style={{ marginBottom: '1.5rem' }}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          一覧に戻る
        </button>
        <div style={{
          borderBottom: '2px solid #e5e7eb',
          paddingBottom: '0.75rem',
          marginBottom: '2rem'
        }}>
          <h1 className="page-title">
            {isNew ? '新規ステータスフロー作成' : 'ステータスフロー詳細'}
          </h1>
        </div>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #e5e7eb',
          color: '#b91c1c',
          padding: '1rem',
          borderRadius: '0.5rem',
          marginBottom: '1.5rem'
        }}>
          {error}
        </div>
      )}

      {/* アクション */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        border: '1px solid #e5e7eb',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">アクション</h2>
        <div className="flex gap-4">
          {!isNew && !isDraft && (
            <button
              onClick={handleDuplicateClick}
              className="btn btn-secondary"
              disabled={saving}
              style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
            >
              複製して作成
            </button>
          )}
          {isDraft && (
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                fontWeight: 500,
                border: '1px solid #2563eb',
                borderRadius: '0.375rem',
                color: 'white',
                backgroundColor: '#2563eb',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.5 : 1,
                transition: 'all 0.15s'
              }}
              onMouseEnter={(e) => {
                if (!saving) {
                  e.currentTarget.style.backgroundColor = '#1d4ed8'
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#2563eb'
              }}
            >
              {saving ? '保存中...' : '保存'}
            </button>
          )}
          <button
            onClick={handleLint}
            disabled={lintLoading}
            className="btn btn-secondary"
            style={{
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
              opacity: lintLoading ? 0.5 : 1,
              cursor: lintLoading ? 'not-allowed' : 'pointer'
            }}
          >
            {lintLoading ? '整合性チェック中...' : '整合性チェック'}
          </button>
          {!isNew && isDraft && (
            <button
              onClick={handlePublish}
              disabled={saving}
              className="btn btn-primary"
              style={{
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                opacity: saving ? 0.5 : 1,
                cursor: saving ? 'not-allowed' : 'pointer'
              }}
            >
              {saving ? '確定中...' : '確定'}
            </button>
          )}
        </div>
      </div>

      {/* Basic Info Card */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        border: '1px solid #e5e7eb',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.3rem' }}>
            <h2 className="text-lg font-semibold text-gray-900">基本情報</h2>
            {workflow && (
              <span style={{
                padding: '0.25rem 0.75rem',
                fontSize: '0.75rem',
                fontWeight: 600,
                borderRadius: '0.375rem',
                backgroundColor: workflow.status === 'PUBLISHED' ? '#dcfce7' :
                                workflow.status === 'ARCHIVED' ? '#fee2e2' : '#f3f4f6',
                color: workflow.status === 'PUBLISHED' ? '#166534' :
                      workflow.status === 'ARCHIVED' ? '#991b1b' : '#374151'
              }}>
                {workflow.status === 'PUBLISHED' ? '確定' :
                 workflow.status === 'ARCHIVED' ? '廃止' : '編集中'}
              </span>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ステータスフローID
            </label>
            <input
              type="text"
              value={workflow?.workflowVersionId || '（保存時に自動生成）'}
              readOnly
              disabled
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                backgroundColor: '#f9fafb',
                color: '#6b7280',
                cursor: 'not-allowed'
              }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ステータスフロー名 <span className="text-red-600">*</span>
            </label>
            <input
              ref={workflowNameInputRef}
              type="text"
              value={workflowName}
              onChange={(e) => {
                setWorkflowName(e.target.value)
                if (workflowNameInputRef.current) {
                  workflowNameInputRef.current.setCustomValidity('')
                }
              }}
              disabled={!isDraft}
              placeholder="休暇申請フロー"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              説明（任意）
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={!isDraft}
              placeholder="このステータスフローの説明"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            />
          </div>

          {workflow && (
            <div style={{ display: 'flex', gap: '2rem', fontSize: '0.875rem' }}>
              <div>
                <span style={{ color: '#6b7280' }}>作成日時:</span>{' '}
                <span style={{ color: '#111827' }}>{new Date(workflow.createdAt).toLocaleString('ja-JP')}</span>
              </div>
              <div>
                <span style={{ color: '#6b7280' }}>更新日時:</span>{' '}
                <span style={{ color: '#111827' }}>{new Date(workflow.updatedAt).toLocaleString('ja-JP')}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        border: '1px solid #e5e7eb',
        marginBottom: '1.5rem'
      }}>
        {/* Tab Headers */}
        <div style={{ borderBottom: '1px solid #e5e7eb' }}>
          <nav style={{ display: 'flex', marginBottom: '-1px' }}>
            <button
              onClick={() => setActiveTab('flow')}
              style={{
                padding: '0.75rem 1.5rem',
                fontSize: '0.875rem',
                fontWeight: 500,
                borderBottom: activeTab === 'flow' ? '2px solid #3b82f6' : '2px solid transparent',
                color: activeTab === 'flow' ? '#3b82f6' : '#6b7280',
                backgroundColor: activeTab === 'flow' ? '#f9fafb' : 'transparent',
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== 'flow') {
                  e.currentTarget.style.color = '#374151'
                  e.currentTarget.style.borderBottomColor = '#d1d5db'
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'flow') {
                  e.currentTarget.style.color = '#6b7280'
                  e.currentTarget.style.borderBottomColor = 'transparent'
                }
              }}
            >
              フロー定義
            </button>
            <button
              onClick={() => setActiveTab('lint')}
              style={{
                padding: '0.75rem 1.5rem',
                fontSize: '0.875rem',
                fontWeight: 500,
                borderBottom: activeTab === 'lint' ? '2px solid #3b82f6' : '2px solid transparent',
                color: activeTab === 'lint' ? '#3b82f6' : '#6b7280',
                backgroundColor: activeTab === 'lint' ? '#f9fafb' : 'transparent',
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== 'lint') {
                  e.currentTarget.style.color = '#374151'
                  e.currentTarget.style.borderBottomColor = '#d1d5db'
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'lint') {
                  e.currentTarget.style.color = '#6b7280'
                  e.currentTarget.style.borderBottomColor = 'transparent'
                }
              }}
            >
              整合性チェック
            </button>
            <button
              onClick={() => setActiveTab('import-export')}
              style={{
                padding: '0.75rem 1.5rem',
                fontSize: '0.875rem',
                fontWeight: 500,
                borderBottom: activeTab === 'import-export' ? '2px solid #3b82f6' : '2px solid transparent',
                color: activeTab === 'import-export' ? '#3b82f6' : '#6b7280',
                backgroundColor: activeTab === 'import-export' ? '#f9fafb' : 'transparent',
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== 'import-export') {
                  e.currentTarget.style.color = '#374151'
                  e.currentTarget.style.borderBottomColor = '#d1d5db'
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'import-export') {
                  e.currentTarget.style.color = '#6b7280'
                  e.currentTarget.style.borderBottomColor = 'transparent'
                }
              }}
            >
              インポート／エクスポート
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div style={{ padding: '1.5rem' }}>
          {/* Flow Definition Tab */}
          {activeTab === 'flow' && (
            <div className="space-y-6">
              {/* States Section */}
              <div>
                <div style={{
                  borderBottom: '2px solid rgb(182 206 233)',
                  paddingBottom: '0.5rem',
                  marginBottom: '1rem'
                }}>
                  <h3 style={{
                    fontSize: '1.125rem',
                    fontWeight: 600,
                    color: '#111827',
                    margin: 0
                  }}>
                    ステータス一覧
                  </h3>
                </div>
                {isDraft && (
                  <div style={{ marginBottom: '1rem' }}>
                    <button
                      onClick={handleAddState}
                      className="btn btn-secondary"
                      style={{ fontSize: '0.875rem', padding: '0.5rem 0.75rem' }}
                    >
                      + ステータス追加
                    </button>
                  </div>
                )}

                {states.length === 0 ? (
                  <div className="text-center text-gray-500 py-4">
                    ステータスがありません
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table className="min-w-full" style={{ borderCollapse: 'collapse', border: '1px solid #e5e7eb', borderRadius: '0.5rem' }}>
                      <thead style={{ backgroundColor: '#f8fafc' }}>
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-900 uppercase" style={{ width: '120px', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderLeft: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>順序</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-900 uppercase" style={{ width: '180px', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>ステータスキー</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>ラベル</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-900 uppercase" style={{ width: '110px', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>背景色</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-900 uppercase" style={{ width: '110px', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>テキスト色</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-900 uppercase" style={{ width: '150px', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>プレビュー</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-900 uppercase" style={{ width: '80px', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>終端</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-900 uppercase" style={{ width: '100px', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>アクション</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        {states.map((state, index) => (
                          <tr key={index}>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500" style={{ width: '120px', borderBottom: '1px solid #e5e7eb', borderLeft: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                <span className="font-medium text-gray-900">{index + 1}</span>
                                {isDraft && (
                                  <div className="flex gap-1">
                                    <button
                                      onClick={() => handleMoveStateUp(index)}
                                      disabled={index === 0}
                                      className="text-gray-600 hover:text-gray-900 disabled:opacity-30"
                                      style={{ fontSize: '1.1rem', fontWeight: 600 }}
                                    >
                                      ↑
                                    </button>
                                    <button
                                      onClick={() => handleMoveStateDown(index)}
                                      disabled={index === states.length - 1}
                                      className="text-gray-600 hover:text-gray-900 disabled:opacity-30"
                                      style={{ fontSize: '1.1rem', fontWeight: 600 }}
                                    >
                                      ↓
                                    </button>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-2" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                              <input
                                type="text"
                                value={state.key}
                                onChange={(e) => handleStateChange(index, 'key', e.target.value.toUpperCase())}
                                disabled={!isDraft}
                                placeholder="DRAFT"
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm disabled:bg-gray-100"
                              />
                            </td>
                            <td className="px-4 py-2" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                              <input
                                type="text"
                                value={state.label}
                                onChange={(e) => handleStateChange(index, 'label', e.target.value)}
                                disabled={!isDraft}
                                placeholder="作成中"
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm disabled:bg-gray-100"
                              />
                            </td>
                            <td className="px-4 py-2" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                              <input
                                type="color"
                                value={state.bgColor}
                                onChange={(e) => handleStateChange(index, 'bgColor', e.target.value)}
                                disabled={!isDraft}
                                className="h-8 border border-gray-300 rounded cursor-pointer disabled:opacity-50"
                                style={{ width: '80%', display: 'block', margin: '0 auto' }}
                              />
                            </td>
                            <td className="px-4 py-2" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                              <input
                                type="color"
                                value={state.textColor}
                                onChange={(e) => handleStateChange(index, 'textColor', e.target.value)}
                                disabled={!isDraft}
                                className="h-8 border border-gray-300 rounded cursor-pointer disabled:opacity-50"
                                style={{ width: '80%', display: 'block', margin: '0 auto' }}
                              />
                            </td>
                            <td className="px-4 py-2" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                              <div style={{
                                display: 'inline-block',
                                padding: '0.25rem 0.75rem',
                                borderRadius: '0.375rem',
                                backgroundColor: state.bgColor,
                                color: state.textColor,
                                fontSize: '0.875rem',
                                fontWeight: '500'
                              }}>
                                {state.label || 'プレビュー'}
                              </div>
                            </td>
                            <td className="px-4 py-2 text-center" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                              <input
                                type="checkbox"
                                checked={state.terminal}
                                onChange={(e) => handleStateChange(index, 'terminal', e.target.checked)}
                                disabled={!isDraft}
                                className="w-4 h-4 cursor-pointer disabled:opacity-50"
                              />
                            </td>
                            <td className="px-4 py-2" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                              {isDraft && (
                                <button
                                  onClick={() => handleRemoveState(index)}
                                  style={{
                                    padding: '0.375rem 0.75rem',
                                    backgroundColor: 'transparent',
                                    color: '#ef4444',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.875rem',
                                    fontWeight: '500',
                                    border: '1px solid #ef4444',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.15s'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                  削除
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  backgroundColor: '#f9fafb',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  color: '#6b7280'
                }}>
                  <strong>ルール:</strong> ステータスキーは大文字英数字とアンダースコアのみ（例: DRAFT, SUBMITTED, APPROVED）
                </div>
              </div>

              {/* Transitions Section */}
              <div style={{ marginTop: '2.25rem' }}>
                <div style={{
                  borderBottom: '2px solid rgb(182 206 233)',
                  paddingBottom: '0.5rem',
                  marginBottom: '1rem'
                }}>
                  <h3 style={{
                    fontSize: '1.125rem',
                    fontWeight: 600,
                    color: '#111827',
                    margin: 0
                  }}>
                    ステータス遷移一覧
                  </h3>
                </div>
                {isDraft && (
                  <div style={{ marginBottom: '1rem' }}>
                    <button
                      onClick={handleAddTransition}
                      className="btn btn-secondary"
                      style={{ fontSize: '0.875rem', padding: '0.5rem 0.75rem' }}
                    >
                      + 遷移追加
                    </button>
                  </div>
                )}

                {transitions.length === 0 ? (
                  <div className="text-center text-gray-500 py-4">
                    遷移がありません
                  </div>
                ) : (
                  <table className="min-w-full" style={{ borderCollapse: 'collapse', border: '1px solid #e5e7eb', borderRadius: '0.5rem' }}>
                    <thead style={{ backgroundColor: '#f8fafc' }}>
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderLeft: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>FROM</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>ACTION</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>TO</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>アクション</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {transitions.map((transition, index) => (
                        <tr key={index}>
                          <td className="px-4 py-2" style={{ borderBottom: '1px solid #e5e7eb', borderLeft: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                            <select
                              value={transition.from}
                              onChange={(e) => handleTransitionChange(index, 'from', e.target.value)}
                              disabled={!isDraft}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm disabled:bg-gray-100"
                            >
                              <option value="">選択...</option>
                              {states.map((state) => (
                                <option key={state.key} value={state.key}>{state.key} ({state.label})</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-4 py-2" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                            <input
                              type="text"
                              value={transition.action || ''}
                              onChange={(e) => handleTransitionChange(index, 'action', e.target.value)}
                              disabled={!isDraft}
                              placeholder="submit"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm disabled:bg-gray-100"
                            />
                          </td>
                          <td className="px-4 py-2" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                            <select
                              value={transition.to}
                              onChange={(e) => handleTransitionChange(index, 'to', e.target.value)}
                              disabled={!isDraft}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm disabled:bg-gray-100"
                            >
                              <option value="">選択...</option>
                              {states.map((state) => (
                                <option key={state.key} value={state.key}>{state.key} ({state.label})</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-4 py-2" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                            {isDraft && (
                              <button
                                onClick={() => handleRemoveTransition(index)}
                                style={{
                                  padding: '0.375rem 0.75rem',
                                  backgroundColor: 'transparent',
                                  color: '#ef4444',
                                  borderRadius: '0.375rem',
                                  fontSize: '0.875rem',
                                  fontWeight: '500',
                                  border: '1px solid #ef4444',
                                  cursor: 'pointer',
                                  transition: 'background-color 0.15s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                削除
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* Import/Export Tab */}
          {activeTab === 'import-export' && (
            <div className="space-y-6">
              {/* Export Section */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">エクスポート</h3>
                <p className="text-sm text-gray-600 mb-4">
                  現在のワークフロー定義をJSONファイルとしてダウンロードします。
                </p>
                <button
                  onClick={handleExport}
                  className="btn btn-primary"
                >
                  JSONファイルをダウンロード
                </button>
              </div>

              {/* Divider */}
              <div style={{ borderTop: '1px solid #e5e7eb' }} />

              {/* Import Section */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">インポート</h3>
                <p className="text-sm text-gray-600 mb-4">
                  JSONファイルを選択してワークフロー定義をインポートします。
                </p>

                {/* File Input */}
                <div className="mb-4">
                  <input
                    type="file"
                    accept="application/json,.json"
                    onChange={handleFileSelect}
                    style={{
                      display: 'block',
                      padding: '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>

                {/* Preview */}
                {importPreview && (
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">プレビュー</h4>
                      <div style={{
                        backgroundColor: '#1f2937',
                        color: '#f3f4f6',
                        padding: '1rem',
                        borderRadius: '0.375rem',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        overflow: 'auto',
                        maxHeight: '300px'
                      }}>
                        <pre>{JSON.stringify(importPreview, null, 2)}</pre>
                      </div>
                    </div>

                    {/* Apply Button */}
                    {isDraft && (
                      <div className="flex justify-end">
                        <button
                          onClick={handleApplyImport}
                          className="btn btn-primary"
                        >
                          適用する
                        </button>
                      </div>
                    )}

                    {!isDraft && (
                      <div style={{
                        padding: '0.75rem',
                        backgroundColor: '#fef3c7',
                        border: '1px solid #fbbf24',
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem',
                        color: '#92400e'
                      }}>
                        確定済みまたはアーカイブ済みのワークフローは編集できません。
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Lint Results Tab */}
          {activeTab === 'lint' && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">整合性チェック結果</h3>
              {!lintResult ? (
                <div className="text-center text-gray-500 py-8">
                  整合性チェックを実行してください
                </div>
              ) : (
                <div className="space-y-4">
                  {/* 検証項目の説明 */}
                  {lintResult.pass && (
                    <div style={{
                      padding: '1rem',
                      backgroundColor: '#f9fafb',
                      borderRadius: '0.375rem',
                      border: '1px solid #e5e7eb'
                    }}>
                      <h4 style={{
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: '#374151',
                        marginBottom: '0.75rem'
                      }}>
                        チェック完了：以下の項目を検証しました
                        {lintResult.warnings && lintResult.warnings.length > 0 ? (
                          <span style={{ color: '#f59e0b', marginLeft: '0.5rem' }}>
                            （警告：{lintResult.warnings.length}件）
                          </span>
                        ) : (
                          <span style={{ color: '#6b7280', marginLeft: '0.5rem' }}>
                            （警告・エラーなし）
                          </span>
                        )}
                      </h4>
                      <ul style={{
                        listStyle: 'none',
                        padding: 0,
                        margin: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.5rem'
                      }}>
                        <li style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#374151' }}>
                          <span style={{ display: 'inline-block', width: '1rem', textAlign: 'center', color: '#10b981', flexShrink: 0 }}>✓</span>
                          <span>ステータス名が重複していないか</span>
                        </li>
                        <li style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#374151' }}>
                          <span style={{ display: 'inline-block', width: '1rem', textAlign: 'center', color: '#10b981', flexShrink: 0 }}>✓</span>
                          <span>ステータスキーが正しい形式か（大文字英数字とアンダースコア）</span>
                        </li>
                        <li style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#374151' }}>
                          <span style={{ display: 'inline-block', width: '1rem', textAlign: 'center', color: '#10b981', flexShrink: 0 }}>✓</span>
                          <span>ステータスの必須項目が入力されているか（ラベル、色など）</span>
                        </li>
                        <li style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#374151' }}>
                          <span style={{ display: 'inline-block', width: '1rem', textAlign: 'center', color: '#10b981', flexShrink: 0 }}>✓</span>
                          <span>遷移の行き先ステータスが存在するか</span>
                        </li>
                        <li style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#374151' }}>
                            <span style={{
                              display: 'inline-block',
                              width: '1rem',
                              textAlign: 'center',
                              color: lintResult.warnings && lintResult.warnings.length > 0 ? '#f59e0b' : '#10b981',
                              flexShrink: 0
                            }}>
                              {lintResult.warnings && lintResult.warnings.length > 0 ? '⚠' : '✓'}
                            </span>
                            <span>到達できないステータスがないか</span>
                          </div>
                          {lintResult.warnings && lintResult.warnings.length > 0 && (
                            <div style={{ marginLeft: '1.5rem', fontSize: '0.75rem', color: '#92400e' }}>
                              {lintResult.warnings.map((warning, index) => (
                                <div key={index}>{warning}</div>
                              ))}
                            </div>
                          )}
                        </li>
                      </ul>
                    </div>
                  )}

                  {/* Errors */}
                  {!lintResult.pass && lintResult.errors && lintResult.errors.length > 0 && (
                    <div style={{
                      padding: '1rem',
                      backgroundColor: '#fee2e2',
                      borderRadius: '0.375rem',
                      border: '1px solid #fecaca'
                    }}>
                      <h4 style={{
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: '#dc2626',
                        marginBottom: '0.75rem'
                      }}>
                        検証失敗：以下のエラーを修正してください（エラー：{lintResult.errors.length}件）
                      </h4>
                      <ul style={{
                        listStyle: 'none',
                        padding: 0,
                        margin: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.5rem'
                      }}>
                        {lintResult.errors.map((error, index) => (
                          <li key={index} style={{ fontSize: '0.875rem', color: '#991b1b' }}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

      {/* 複製確認ダイアログ */}
      {showDuplicateConfirm && workflow && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-semibold mb-4">複製確認</h2>
            <p className="text-gray-700 mb-2">
              以下のステータスフローを複製して新しい下書きを作成しますか？
            </p>
            <div className="bg-gray-50 p-3 rounded mb-4">
              <div className="text-sm">
                <div><strong>ステータスフロー名:</strong> {workflow.workflowName}</div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button
                onClick={() => setShowDuplicateConfirm(false)}
                disabled={saving}
                className="btn btn-secondary"
              >
                キャンセル
              </button>
              <button
                onClick={handleDuplicateConfirm}
                disabled={saving}
                className="btn btn-primary"
                style={saving ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
              >
                {saving ? '複製中...' : '複製して作成'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  )
}
